import React, { useEffect, useRef, useState } from "react";

import "./RichTextEditor.css";

// ------------------------------------------------------------------ //
//  CKEditor 5 (ClassicEditor) loaded from the official CDN. We deliberately do
//  NOT bundle any @ckeditor npm build: having a bundled CKEditor 5 (the old
//  decoupled build) on the page at the same time as the CDN copy makes CKEditor
//  throw "ckeditor-duplicated-modules" and silently fall back — which is exactly
//  what hid the source-editing button before. We pin v43.3.1 (no license key
//  required — see below). Image upload uses the official
//  SimpleUploadAdapter -> our /upload endpoint. SourceEditing (the < > button) +
//  GeneralHtmlSupport give native view/paste/edit of raw HTML. If the CDN is
//  unreachable we fall back to a plain HTML <textarea>.
// ------------------------------------------------------------------ //
// v43.3.1 is the LAST CKEditor 5 release before the mandatory license key
// (v44+). With v44+ and a key the CDN build won't accept, the editor renders but
// silently goes READ-ONLY (you can't type) — that was the bug. 43.3.1 needs no
// license and still ships SourceEditing (the < > button) + GeneralHtmlSupport.
const CK_VERSION = "43.3.1";
const CK_JS = `https://cdn.ckeditor.com/ckeditor5/${CK_VERSION}/ckeditor5.umd.js`;
const CK_CSS = `https://cdn.ckeditor.com/ckeditor5/${CK_VERSION}/ckeditor5.css`;

let cdnPromise = null;
const loadCkCdn = () => {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.CKEDITOR && window.CKEDITOR.ClassicEditor)
    return Promise.resolve(window.CKEDITOR);
  if (cdnPromise) return cdnPromise;
  cdnPromise = new Promise((resolve, reject) => {
    let link = null; // only the <link> THIS attempt created (so retries clean up)
    let script = null;
    const fail = (msg) => {
      // Reset so the next mount retries with fresh tags rather than reusing a
      // rejected promise or orphaned/failed tags.
      cdnPromise = null;
      if (script && script.parentNode) script.parentNode.removeChild(script);
      if (link && link.parentNode) link.parentNode.removeChild(link);
      reject(new Error(msg));
    };
    try {
      if (!document.querySelector("link[data-ck5cdn]")) {
        link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = CK_CSS;
        link.setAttribute("data-ck5cdn", "1");
        document.head.appendChild(link);
      }
      script = document.createElement("script");
      script.src = CK_JS;
      script.async = true;
      script.setAttribute("data-ck5cdn", "1");
      script.onload = () => {
        if (window.CKEDITOR && window.CKEDITOR.ClassicEditor) {
          resolve(window.CKEDITOR);
        } else {
          fail("CKEditor global missing after CDN load");
        }
      };
      script.onerror = () => fail("CKEditor CDN failed to load");
      document.head.appendChild(script);
    } catch (e) {
      fail((e && e.message) || "CKEditor CDN load error");
    }
  });
  return cdnPromise;
};

// Defence-in-depth: the public site already sanitises CMS HTML on render, but
// the editor itself must never EMIT active content. The CDN editor's GHS config
// already drops script/style/on*, and the old decoupled fallback stripped them
// implicitly — but the plain-<textarea> fallback would pass raw HTML straight
// through, so strip dangerous content here on the way out. Mirrors the frontend
// sanitiser's core rules.
function stripDangerousHtml(html) {
  if (!html) return "";
  return html
    .replace(
      /<\s*(script|style|object|embed|noscript|template|form|input|button|svg|math|link|meta|base)\b[\s\S]*?<\s*\/\s*\1\s*>/gi,
      ""
    )
    .replace(
      /<\s*\/?\s*(script|style|object|embed|noscript|template|form|input|button|svg|math|link|meta|base)\b[^>]*>/gi,
      ""
    )
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, " ")
    .replace(/\/+on\w+\s*=\s*[^\s>"']+/gi, " ")
    .replace(
      /\s(href|src|xlink:href)\s*=\s*("\s*(?:javascript|vbscript):[^"]*"|'\s*(?:javascript|vbscript):[^']*'|(?:javascript|vbscript):[^\s>]+)/gi,
      ' $1="#"'
    );
}

const RichTextEditor = ({ value, onChange, uploadUrl, token }) => {
  const elRef = useRef(null);
  const editorRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const editorValueRef = useRef(value || "");
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const [mode, setMode] = useState("loading"); // loading | cdn | fallback
  const [fallbackValue, setFallbackValue] = useState(value || "");

  useEffect(() => {
    let cancelled = false;
    loadCkCdn()
      .then((CK) => {
        if (cancelled || !elRef.current) return null;
        const {
          ClassicEditor,
          Essentials,
          Paragraph,
          Heading,
          Bold,
          Italic,
          Underline,
          Strikethrough,
          Link,
          List,
          BlockQuote,
          Alignment,
          Indent,
          IndentBlock,
          FontSize,
          FontColor,
          FontBackgroundColor,
          Image,
          ImageToolbar,
          ImageCaption,
          ImageStyle,
          ImageResize,
          ImageInsert,
          ImageUpload,
          SimpleUploadAdapter,
          Table,
          TableToolbar,
          MediaEmbed,
          HorizontalLine,
          RemoveFormat,
          PasteFromOffice,
          Autoformat,
          TextTransformation,
          SourceEditing,
          GeneralHtmlSupport,
        } = CK;
        return ClassicEditor.create(elRef.current, {
          plugins: [
            Essentials,
            Paragraph,
            Heading,
            Bold,
            Italic,
            Underline,
            Strikethrough,
            Link,
            List,
            BlockQuote,
            Alignment,
            Indent,
            IndentBlock,
            FontSize,
            FontColor,
            FontBackgroundColor,
            Image,
            ImageToolbar,
            ImageCaption,
            ImageStyle,
            ImageResize,
            ImageInsert,
            ImageUpload,
            SimpleUploadAdapter,
            Table,
            TableToolbar,
            MediaEmbed,
            HorizontalLine,
            RemoveFormat,
            PasteFromOffice,
            Autoformat,
            TextTransformation,
            SourceEditing,
            GeneralHtmlSupport,
          ].filter(Boolean),
          toolbar: {
            items: [
              "undo",
              "redo",
              "|",
              "heading",
              "|",
              "fontSize",
              "fontColor",
              "fontBackgroundColor",
              "|",
              "bold",
              "italic",
              "underline",
              "strikethrough",
              "|",
              "link",
              "insertImage",
              "insertTable",
              "mediaEmbed",
              "blockQuote",
              "horizontalLine",
              "|",
              "bulletedList",
              "numberedList",
              "outdent",
              "indent",
              "|",
              "alignment",
              "removeFormat",
              "|",
              "sourceEditing",
            ],
            shouldNotGroupWhenFull: true,
          },
          heading: {
            options: [
              {
                model: "paragraph",
                title: "Paragraph",
                class: "ck-heading_paragraph",
              },
              {
                model: "heading2",
                view: "h2",
                title: "Heading",
                class: "ck-heading_heading2",
              },
              {
                model: "heading3",
                view: "h3",
                title: "Subheading",
                class: "ck-heading_heading3",
              },
              {
                model: "heading4",
                view: "h4",
                title: "Small heading",
                class: "ck-heading_heading4",
              },
            ],
          },
          image: {
            toolbar: [
              "imageTextAlternative",
              "toggleImageCaption",
              "|",
              "imageStyle:inline",
              "imageStyle:wrapText",
              "imageStyle:breakText",
              "|",
              "resizeImage",
            ],
          },
          table: {
            contentToolbar: ["tableColumn", "tableRow", "mergeTableCells"],
          },
          link: { addTargetToExternalLinks: true },
          simpleUpload: {
            uploadUrl,
            headers: { authorization: tokenRef.current },
          },
          placeholder: "Start typing page content...",
          // General HTML Support: preserve arbitrary pasted/source-edited HTML
          // (structure, classes, inline styles, trusted iframes) instead of
          // dropping tags the model has no dedicated plugin for. Active content
          // (script/style/on* handlers) is disallowed to match the public
          // site's sanitiser.
          htmlSupport: {
            allow: [
              {
                name: /^(section|article|aside|header|footer|nav|div|p|span|h[1-6]|ul|ol|li|dl|dt|dd|a|img|figure|figcaption|picture|source|table|thead|tbody|tfoot|tr|td|th|caption|colgroup|col|blockquote|pre|code|strong|b|em|i|u|s|mark|small|sup|sub|br|hr|iframe|video|audio)$/,
                attributes: true,
                classes: true,
                styles: true,
              },
            ],
            disallow: [
              { name: "script" },
              { name: "style" },
              { name: /.*/, attributes: /^on.*/i },
            ],
          },
          initialData: editorValueRef.current || "",
        });
      })
      .then((editor) => {
        if (!editor) return;
        if (cancelled) {
          try {
            editor.destroy();
          } catch (e) {
            /* ignore */
          }
          return;
        }
        editorRef.current = editor;
        setMode("cdn");
        editor.model.document.on("change:data", () => {
          const nextValue = stripDangerousHtml(editor.getData());
          editorValueRef.current = nextValue;
          if (onChangeRef.current) onChangeRef.current(nextValue);
        });
      })
      .catch((err) => {
        // CDN blocked / CSP / offline / license or API change — fall back to the
        // bundled editor, but surface WHY so it can be diagnosed (it was silently
        // swallowed before, which hid the real cause).
        // eslint-disable-next-line no-console
        console.error(
          "[CMS editor] CKEditor 5 (CDN) failed to initialise; using the basic fallback editor. Reason:",
          err
        );
        if (!cancelled) setMode("fallback");
      });

    return () => {
      cancelled = true;
      if (editorRef.current) {
        try {
          editorRef.current.destroy();
        } catch (e) {
          /* ignore */
        }
        editorRef.current = null;
      }
    };
    // Create exactly once per mount; the modal remounts this on each open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const nextValue = value || "";
    setFallbackValue(nextValue);

    const editor = editorRef.current;
    if (!editor) {
      editorValueRef.current = nextValue;
      return;
    }

    if (
      nextValue !== editorValueRef.current &&
      nextValue !== editor.getData()
    ) {
      editor.setData(nextValue);
    }

    editorValueRef.current = nextValue;
  }, [value]);

  return (
    <div className="cms-ck5">
      {mode === "loading" && (
        <div className="cms-ck5-loading">Loading editor…</div>
      )}
      {mode === "fallback" && (
        // CDN unreachable — a plain HTML textarea still lets the admin write /
        // paste raw HTML, which is exactly the feature that matters.
        <textarea
          className="cms-html-source"
          value={fallbackValue}
          spellCheck={false}
          placeholder="Paste or edit HTML here…"
          onChange={(e) => {
            setFallbackValue(e.target.value);
            if (onChangeRef.current)
              onChangeRef.current(stripDangerousHtml(e.target.value));
          }}
        />
      )}
      {/* Keep the source element displayable during init so CKEditor can lay itself out. */}
      <div
        ref={elRef}
        className="cms-ck5-source"
        style={{ display: mode === "fallback" ? "none" : undefined }}
      />
    </div>
  );
};

export default RichTextEditor;
